import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay } from "@fortawesome/free-solid-svg-icons";
import { useDispatch } from "react-redux";
import { useAxiosInstance } from "../../services/Interceptor";
import { toast } from "react-toastify";
import PremiumFeatureModal from "../PremiumFeatureModal";
import FreeTrialModal from "../FreeTrialModal";
import { StartFreeTrial } from "../../services/PaymentService";
import { setProfileAction } from "../../store/AthleteActions";
import { validateSubscriptionAction } from "../../store/userActions";
import { bumpMenuRefresh } from "../../store/slices/athleteSlice";
import { useSubscriptionAccess } from "../../hooks/useSubscriptionAccess";
import { usePremiumUpsellRedirect } from "../../hooks/usePremiumUpsellRedirect";

export default function VideoPlayer({
  src,
  width = "250px",
  height = "150px",
  overlay = true,
  playbackRate = 1,
  registerVideoRef,
  onVideoPlay,
  onVideoPause,
  onVideoEnded,
}) {
  const [playing, setPlaying] = useState(false);
  const [openPremiumFeatureModal, setOpenPremiumFeatureModal] = useState(false);
  const [openTrialModal, setOpenTrialModal] = useState(false);
  const videoRef = useRef(null);
  const playPromiseRef = useRef(null);

  const dispatch = useDispatch();
  const apiInstance = useAxiosInstance();
  const { user, hasSubscriptionAccess, showFreeTrial } =
    useSubscriptionAccess();

  const openSubscription = usePremiumUpsellRedirect(user);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.defaultPlaybackRate = playbackRate;
      videoRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const handlePremiumFreeTrial = () => {
    setOpenPremiumFeatureModal(false);
    setOpenTrialModal(true);
  };

  const handlePremiumSubscribe = () => {
    setOpenPremiumFeatureModal(false);
    openSubscription();
  };

  const handlePlay = () => {
    if (!hasSubscriptionAccess) {
      setOpenPremiumFeatureModal(true);
      return;
    }
    setPlaying(true);
    if (videoRef.current) {
      const promise = videoRef.current.play();
      if (promise !== undefined) {
        playPromiseRef.current = promise;
        promise.then(
          () => {
            playPromiseRef.current = null;
          },
          () => {
            playPromiseRef.current = null;
          },
        );
      }
    }
  };

  return (
    <>
      <PremiumFeatureModal
        open={openPremiumFeatureModal}
        onClose={() => setOpenPremiumFeatureModal(false)}
        title="Premium Feature"
        description="Video playback is locked. Start a free trial or subscribe now to watch videos."
        onStartFreeTrial={handlePremiumFreeTrial}
        onSubscribe={handlePremiumSubscribe}
        showStartFreeTrial={showFreeTrial}
      />
      <FreeTrialModal
        open={openTrialModal}
        onClose={() => setOpenTrialModal(false)}
        user={user}
        onStoreSuccessChain={async () => {
          const result = await StartFreeTrial(apiInstance, user.id);
          if (result.success) {
            toast.success("Free trial started!", { position: "top-right" });
            setOpenTrialModal(false);
            await dispatch(setProfileAction(apiInstance, user.id));
            await dispatch(
              validateSubscriptionAction(apiInstance, user.role, user.id),
            );
            dispatch(bumpMenuRefresh());
          } else {
            toast.error("Failed to start free trial", {
              position: "top-right",
            });
          }
        }}
      />
      <div
        className="relative rounded-md overflow-hidden bg-black"
        style={{ width, height }}
      >
        <video
          ref={(element) => {
            videoRef.current = element;
            if (element) {
              registerVideoRef?.({
                pause: () => {
                  if (playPromiseRef.current !== null) {
                    playPromiseRef.current
                      .then(() => element.pause())
                      .catch(() => {});
                  } else if (!element.paused) {
                    element.pause();
                  }
                },
              });
            } else {
              registerVideoRef?.(null);
            }
          }}
          src={`${src}#t=0.001`}
          preload="metadata"
          controls={hasSubscriptionAccess}
          playsInline
          className="w-full h-full"
          style={hasSubscriptionAccess ? undefined : { pointerEvents: "none" }}
          onPlay={() => {
            if (!hasSubscriptionAccess) {
              videoRef.current?.pause();
              setOpenPremiumFeatureModal(true);
              return;
            }
            setPlaying(true);
            onVideoPlay?.();
          }}
          onPause={() => {
            setPlaying(false);
            onVideoPause?.();
          }}
          onEnded={() => {
            setPlaying(false);
            onVideoEnded?.();
          }}
        />
        {overlay && !playing && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer"
            onClick={handlePlay}
          >
            <FontAwesomeIcon
              icon={faPlay}
              className="text-white text-3xl opacity-90"
            />
          </div>
        )}
      </div>
    </>
  );
}
